/**
 * Renders a single post of any type (post / event / offer).
 *
 * `isNested` — suppresses action buttons and translation-boost indicator; used when PostCard
 * is embedded inside BoostCard so the nested post doesn't render its own interactive row.
 *
 * `readOnly` — disables the owner edit/delete menu and all action buttons (used on detail pages).
 */
import { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import {
  Flag,
  Gift,
  Handshake,
  MoreHorizontal,
  Trash2,
  Pencil,
  Loader2,
  Globe,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Link2,
  Languages,
  Users,
} from 'lucide-react'
import type { Post, EventPost, OfferPost, ActionPost, FeedItem, Language } from '@olimhub/shared'
import { LANGUAGES, SYSTEM_BADGE_SLUGS } from '@olimhub/shared'
import { Avatar } from '@/components/profile/Avatar'
import { LanguageBadge } from '@/components/common/LanguageBadge'
import { renderContent } from '@/lib/content-renderer'
import { formatPostTime } from '@/lib/dates'
import { accountPath } from '@/lib/accountPath'
import { useAuthStore } from '@/store/auth.store'
import { postsApi, adminApi, translationsApi } from '@/api'
import type { AnyPost } from '@/api/endpoints/posts'
import { useToast } from '@/components/common/Toast'
import { usePostForm } from '@/context/PostFormContext'
import { BoostButton } from './BoostButton'
import { ClapButton } from './ClapButton'
import { ShareButton } from './ShareButton'
import { ClappingIcon } from './ClappingIcon'
import { TranslationBoostButton } from './TranslationBoostButton'
import { TranslationBoostModal } from './TranslationBoostModal'
import { ReportModal } from '@/components/common/ReportModal'
import { LinkPreview } from './LinkPreview'
import { EventMeta } from './EventMeta'
import { ActionMeta } from './ActionMeta'
import { CategoryChip } from './CategoryChip'
import { ActionRsvpButton } from './ActionRsvpButton'
import { TranslationCard } from './TranslationCard'
import { ReplyList } from './ReplyList'
import { PostBadge } from './PostBadge'
import { TagPills } from './TagPills'
import { feedScroll } from '@/lib/feedScroll'

interface InfiniteData {
  pages: Array<{ items: FeedItem[]; nextCursor: string | null }>
  pageParams: unknown[]
}

// ─── PostBody ────────────────────────────────────────────────────────────────

function PostBody({ html, dir = 'ltr' }: { html: string; dir?: 'ltr' | 'rtl' }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const ref = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [clipped, setClipped] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (el) {
      setClipped(el.scrollHeight - el.clientHeight > 2)
    }
  }, [html])

  function handleContentClick(e: React.MouseEvent<HTMLDivElement>) {
    const target = e.target as HTMLElement
    if (target.tagName === 'A') {
      e.stopPropagation()
      if (target.classList.contains('md-tag')) {
        e.preventDefault()
        navigate(`/?tag=${target.dataset.tag ?? ''}`)
      } else if (target.classList.contains('md-mention')) {
        e.preventDefault()
        navigate(`/@${target.dataset.username ?? ''}`)
      }
    }
  }

  return (
    <div>
      <div
        ref={ref}
        dir={dir}
        onClick={handleContentClick}
        dangerouslySetInnerHTML={{ __html: html }}
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-body)',
          color: 'var(--text-body)',
          lineHeight: 'var(--lh-normal)',
          wordBreak: 'break-word',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: expanded ? 'unset' : 3,
        }}
      />
      {(clipped || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            border: 'none',
            background: 'none',
            padding: '4px 0 0',
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-sm)',
            fontWeight: 600,
            color: 'var(--brand)',
          }}
        >
          {expanded ? t('post.show_less') : t('post.show_more')}
        </button>
      )}
    </div>
  )
}


// ─── PostCard ────────────────────────────────────────────────────────────────

export interface PostCardProps {
  post: Post | EventPost | OfferPost
  readOnly?: boolean
  isNested?: boolean
  defaultRepliesOpen?: boolean
  isDetailPage?: boolean
  onBoost?: (id: string) => void
  onReport?: (id: string) => void
  onEdit?: (post: AnyPost) => void
  onDeleted?: (id: string) => void
  hideGroupAttribution?: boolean
  // Set by GroupPage when the viewer is that group's group_admin — lets
  // them edit/delete posts authored by the group's mirror account, which
  // `isOwn` alone can't detect (post.account.id is the mirror account, not
  // the admin's own account). Backend already enforces this (edit_group
  // capability); this only controls whether the menu is shown.
  canManage?: boolean
}

export function PostCard({
  post,
  readOnly = false,
  isNested = false,
  defaultRepliesOpen = false,
  isDetailPage = false,
  onReport,
  onEdit,
  onDeleted,
  hideGroupAttribution = false,
  canManage = false,
}: PostCardProps) {
  const { t } = useTranslation()
  const { account: authAccount, isAuthenticated } = useAuthStore()
  const { toast } = useToast()
  const { openEdit } = usePostForm()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()

  const isEvent = post.type === 'event'
  const isOffer = post.type === 'offer'
  const isAction = post.type === 'action'
  const offerType = isOffer && (post as OfferPost).offer ? (post as OfferPost).offer.offerType : null
  const isOwn = !!authAccount && authAccount.id === post.account.id
  // Only for the edit/delete menu — other isOwn-gated UI (boost button,
  // claps-received stat) is about literal authorship, not admin management.
  const canManagePost = isOwn || canManage

  const groupAttribution = post.account.accountType === 'group'
    ? { name: post.account.displayName, path: accountPath(post.account) }
    : post.groupTag
      ? { name: post.groupTag.name, path: `/groups/${post.groupTag.slug}` }
      : null
  const isAdmin = !!authAccount?.badges.some(
    (b) => b.badge.slug === SYSTEM_BADGE_SLUGS.ADMIN || b.badge.slug === SYSTEM_BADGE_SLUGS.SUPER_ADMIN,
  )

  const borderStartColor = isEvent
    ? 'var(--accent)'
    : isOffer
    ? 'var(--bazaar)'
    : isAction
    ? 'var(--action)'
    : undefined

  const html = renderContent(post.content)
  const dir: 'ltr' | 'rtl' = post.language === 'he' ? 'rtl' : 'ltr'

  const [showTranslationModal, setShowTranslationModal] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showTranslationBoosts, setShowTranslationBoosts] = useState(false)
  const [repliesOpen, setRepliesOpen] = useState(defaultRepliesOpen)
  const [showEditLang, setShowEditLang] = useState(false)
  const [editLang, setEditLang] = useState<Language>(post.language as Language)
  const [isSavingLang, setIsSavingLang] = useState(false)
  // Optimistic local count so a just-created translation shows up immediately,
  // same pattern as BoostButton's optimisticCount — no cache-wide refetch needed.
  const [translationCount, setTranslationCount] = useState(post.translationBoostCount ?? 0)

  // Fetch translations only when expanded
  const { data: translationsData } = useQuery({
    queryKey: ['post-translations', post.id],
    queryFn: () => translationsApi.list(post.id, undefined, 10),
    enabled: showTranslationBoosts && translationCount > 0,
  })

  const translations = translationsData?.items.filter((tr) => !tr.post.hiddenByAuthor) ?? []

  const loginRedirect = () => {
    navigate(`/login?from=${encodeURIComponent(location.pathname)}`)
  }

  const copyLink = () => {
    void navigator.clipboard.writeText(window.location.origin + '/posts/' + post.id)
    toast.success('Link copied!')
  }

  const handleEdit = () => {
    setShowMenu(false)
    if (onEdit) {
      onEdit(post as AnyPost)
    } else {
      openEdit(post as AnyPost)
    }
  }

  const handleDeleteConfirm = async () => {
    setIsDeleting(true)
    try {
      await postsApi.delete(post.id)
      toast.success('Post deleted')
      const removeFromCache = (old: InfiniteData | undefined) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.filter(
              (item) => !(item.kind === 'post' && (item.data as Post).id === post.id),
            ),
          })),
        }
      }
      queryClient.setQueriesData<InfiniteData>({ queryKey: ['feed'] }, removeFromCache)
      // Group-authored posts (group_admin managing the group's own content)
      // may be showing in a GroupPage feed — that query key is prefixed
      // differently (['group-feed', slug, ...]), so it needs its own pass.
      queryClient.setQueriesData<InfiniteData>({ queryKey: ['group-feed'] }, removeFromCache)
      onDeleted?.(post.id)
    } catch {
      toast.error('Could not delete post.')
    } finally {
      setIsDeleting(false)
      setConfirmDelete(false)
      setShowMenu(false)
    }
  }

  const handleSaveLanguage = async () => {
    setIsSavingLang(true)
    try {
      await adminApi.updatePostLanguage(post.id, editLang)
      toast.success('Language updated')
      queryClient.invalidateQueries({ queryKey: ['feed'] })
      queryClient.invalidateQueries({ queryKey: ['post', post.id] })
      setShowEditLang(false)
    } catch {
      toast.error('Could not update language.')
    } finally {
      setIsSavingLang(false)
    }
  }

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('a, button, textarea, input, select, [role="button"]')) return
    feedScroll.save(document.getElementById('feed-scroll')?.scrollTop ?? 0)
    navigate(`/posts/${post.id}`)
  }

  return (
    <div
      onClick={handleCardClick}
      style={{
        position: 'relative',
        background: 'var(--surface)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        isolation: 'isolate',
        cursor: 'pointer',
        zIndex: showMenu ? 1 : undefined,
        ...(borderStartColor
          ? {
              borderInlineStartWidth: 4,
              borderInlineStartStyle: 'solid',
              borderInlineStartColor: borderStartColor,
            }
          : {}),
      }}
    >
      {/* Group post attribution band — reuses the Translation Boost header
          band visual language (surface bg, centered text) per PROJECT.md §7.
          Covers both group-authored posts (account.accountType='group') and
          member posts tagged into a group's feed (post.groupTag). */}
      {groupAttribution && !hideGroupAttribution && (
        <div
          onClick={(e) => { e.stopPropagation(); navigate(groupAttribution.path) }}
          style={{
            background: 'var(--surface-2)',
            borderRadius: 'calc(var(--radius-md) - 1px) calc(var(--radius-md) - 1px) 0 0',
            padding: '6px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            cursor: 'pointer',
          }}
        >
          <Users size={13} color="var(--brand)" style={{ flexShrink: 0 }} />
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-muted)' }}>
            {t('groups.posted_in', { name: groupAttribution.name })}
          </span>
        </div>
      )}

      {/* Offer watermarks — clipped to card bounds by their own overflow:hidden wrapper */}
      {isOffer && (offerType === 'good' || offerType === 'service') && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            borderRadius: 'var(--radius-md)',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: -14,
              insetInlineEnd: -10,
              opacity: 0.06,
              color: 'var(--text-strong)',
            }}
          >
            {offerType === 'good' ? <Gift size={108} /> : <Handshake size={108} />}
          </div>
        </div>
      )}

      {/* Content wrapper */}
      <div style={{ position: 'relative', zIndex: 1, paddingBottom: readOnly || isNested ? 16 : 0 }}>
        {/* Header */}
        <div
          style={{
            padding: '14px 16px 0',
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
          }}
        >
          <button
            type="button"
            onClick={() => navigate(accountPath(post.account))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0, display: 'flex' }}
          >
            <Avatar
              name={post.account.displayName || post.account.username}
              avatarUrl={post.account.avatarUrl}
              animalAvatar={post.account.animalAvatar}
              accountType={post.account.accountType}
              size="md"
            />
          </button>

          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Row 1: displayName + badge + menu */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0 }}>
                <button
                  type="button"
                  onClick={() => navigate(accountPath(post.account))}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    fontFamily: 'var(--font-sans)',
                    fontWeight: 700,
                    color: 'var(--text-strong)',
                    fontSize: 'var(--fs-body)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'start',
                  }}
                >
                  {post.account.displayName || post.account.username}
                </button>
                <PostBadge badges={post.account.badges} />
              </div>
              <div style={{ marginInlineStart: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                {/* Shown only within a group's own feed (hideGroupAttribution
                    doubles as "we're in that context") — lets a group_admin
                    see at a glance which posts are also cross-posted. */}
                {hideGroupAttribution && post.showInLocalFeed && (
                  <span title={t('groups.also_in_local')} style={{ display: 'flex', color: 'var(--text-faint)' }}>
                    <Globe size={14} />
                  </span>
                )}
                <LanguageBadge lang={post.language} size="sm" />
                {isOwn && post.receivedClaps && (
                  <ClappingIcon size={16} fill="var(--brand)" />
                )}
                {((isAdmin && !isOwn) || canManagePost) && !readOnly && (
                  <div style={{ position: 'relative' }}>
                    <button
                      type="button"
                      aria-label="Post options"
                      onClick={() => { setShowMenu((v) => !v); setShowEditLang(false); setConfirmDelete(false) }}
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-muted)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 4,
                        borderRadius: 'var(--radius-sm)',
                      }}
                    >
                      <MoreHorizontal size={16} />
                    </button>

                    {showMenu && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          insetInlineEnd: 0,
                          zIndex: 50,
                          background: 'var(--surface)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-md)',
                          boxShadow: 'var(--shadow-modal)',
                          minWidth: 160,
                          overflow: 'hidden',
                        }}
                      >
                        {[
                          ...(isAdmin && !isOwn
                            ? [{
                                key: 'edit-lang',
                                icon: <Languages size={14} />,
                                label: 'Edit language',
                                danger: false,
                                onClick: () => { setShowMenu(false); setEditLang(post.language as Language); setShowEditLang(true) },
                              }]
                            : []),
                          ...(canManagePost
                            ? [
                                { key: 'edit', icon: <Pencil size={14} />, label: t('post.edit'), danger: false, onClick: handleEdit },
                                { key: 'copy-link', icon: <Link2 size={14} />, label: t('post.copy_link'), danger: false, onClick: () => { copyLink(); setShowMenu(false) } },
                                { key: 'delete', icon: <Trash2 size={14} />, label: t('post.delete'), danger: true, onClick: () => { setConfirmDelete(true); setShowMenu(false) } },
                              ]
                            : []),
                        ].map((item, i) => (
                          <button
                            key={item.key}
                            type="button"
                            onClick={item.onClick}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '10px 14px',
                              border: 'none',
                              borderTop: i > 0 ? '1px solid var(--border)' : 'none',
                              background: 'none',
                              cursor: 'pointer',
                              fontFamily: 'var(--font-sans)',
                              fontSize: 'var(--fs-sm)',
                              color: item.danger ? 'var(--danger)' : 'var(--text-body)',
                              textAlign: 'start',
                            }}
                          >
                            {item.icon}
                            {item.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Row 2: @username · time */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                marginTop: 2,
              }}
            >
              <button
                type="button"
                onClick={() => navigate(accountPath(post.account))}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-muted)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                @{post.account.username}
              </button>
              <span style={{ color: 'var(--text-faint)', fontSize: 'var(--fs-sm)' }}>·</span>
              <Link
                to={`/posts/${post.id}`}
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-faint)',
                  flexShrink: 0,
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline' }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none' }}
              >
                {formatPostTime(post.createdAt)}
              </Link>
            </div>
          </div>
        </div>

        {/* Delete confirmation */}
        {confirmDelete && (
          <div
            style={{
              margin: '10px 16px 0',
              padding: '12px 14px',
              background: 'color-mix(in srgb, var(--danger) 8%, var(--surface))',
              border: '1px solid color-mix(in srgb, var(--danger) 30%, var(--border))',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <p
              style={{
                margin: '0 0 10px',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-sm)',
                color: 'var(--text-body)',
              }}
            >
              {t('post.delete_confirm')}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                style={{
                  padding: '7px 14px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--surface)',
                  color: 'var(--text-muted)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={handleDeleteConfirm}
                style={{
                  padding: '7px 14px',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--danger)',
                  color: '#fff',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  fontWeight: 600,
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  opacity: isDeleting ? 0.7 : 1,
                }}
              >
                {isDeleting && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}
                {t('post.delete')}
              </button>
            </div>
          </div>
        )}

        {/* Admin: edit language picker */}
        {showEditLang && (
          <div
            style={{
              margin: '10px 16px 0',
              padding: '12px 14px',
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <p style={{ margin: '0 0 10px', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-strong)' }}>
              Edit language tag
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
              {LANGUAGES.filter((l) => l !== 'he').map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setEditLang(lang as Language)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 'var(--radius-pill)',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-sans)',
                    fontWeight: 700,
                    fontSize: 'var(--fs-xs)',
                    background: editLang === lang ? 'var(--brand)' : 'var(--surface)',
                    color: editLang === lang ? 'var(--on-brand)' : 'var(--text-muted)',
                    outline: editLang === lang ? 'none' : '1px solid var(--border)',
                  }}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setShowEditLang(false)}
                style={{
                  padding: '7px 14px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--surface)',
                  color: 'var(--text-muted)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSavingLang || editLang === post.language}
                onClick={() => { void handleSaveLanguage() }}
                style={{
                  padding: '7px 14px',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--brand)',
                  color: 'var(--on-brand)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  fontWeight: 600,
                  cursor: isSavingLang || editLang === post.language ? 'not-allowed' : 'pointer',
                  opacity: isSavingLang || editLang === post.language ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {isSavingLang && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}
                Save
              </button>
            </div>
          </div>
        )}

        {/* Body */}
        <div style={{ padding: '10px 16px 0' }}>
          <PostBody html={html} dir={dir} />
        </div>

        {/* Image */}
        {post.imageUrl && (
          <div style={{ padding: '12px 16px 0' }}>
            <img
              src={post.imageUrl}
              alt=""
              style={{
                width: '100%',
                maxHeight: 480,
                objectFit: 'contain',
                borderRadius: 'var(--radius-md)',
                display: 'block',
                border: '1px solid var(--border)',
                background: 'var(--bg-sunk)',
              }}
            />
          </div>
        )}

        {/* Link preview */}
        <LinkPreview content={post.content} hasImage={!!post.imageUrl} />

        {/* Event meta */}
        {isEvent && (post as EventPost).event && <EventMeta event={(post as EventPost).event} category={post.category} distanceKm={post.distanceKm} />}

        {/* Offer category pill (Bazaar has no dedicated meta box, only a watermark icon) */}
        {isOffer && post.category && (
          <div style={{ padding: '8px 16px 0' }}>
            <CategoryChip category={post.category} />
          </div>
        )}

        {/* Action meta + RSVP button, right after the details */}
        {isAction && (post as ActionPost).action && (
          <>
            <ActionMeta action={(post as ActionPost).action} category={post.category} distanceKm={post.distanceKm} />
            {!readOnly && !isNested && <ActionRsvpButton post={post as ActionPost} />}
          </>
        )}

        {/* Tag pills */}
        {post.tags && post.tags.length > 0 && <TagPills tags={post.tags} />}

        {/* Action row — always visible; unauthenticated users are redirected to login */}
        {!readOnly && !isNested && (
          <div
            style={{
              padding: '6px 10px 8px',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {/* Reply button */}
            <button
              type="button"
              aria-label="Replies"
              onClick={() => setRepliesOpen((v) => !v)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '7px 9px',
                border: 'none',
                background: repliesOpen ? 'color-mix(in srgb, var(--brand) 10%, transparent)' : 'none',
                cursor: 'pointer',
                borderRadius: 'var(--radius-pill)',
                color: repliesOpen ? 'var(--brand)' : 'var(--text-muted)',
                transition: 'background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)',
              }}
              onMouseEnter={(e) => {
                if (!repliesOpen) e.currentTarget.style.background = 'color-mix(in srgb, var(--brand) 10%, transparent)'
              }}
              onMouseLeave={(e) => {
                if (!repliesOpen) e.currentTarget.style.background = 'none'
              }}
            >
              <MessageCircle size={16} />
              {(post.replyCount ?? 0) > 0 && (
                <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', fontWeight: 500 }}>
                  {post.replyCount}
                </span>
              )}
            </button>

            <BoostButton
              postId={post.id}
              boostId={null}
              count={post.boostCount}
              isBoosted={post.isBoosted ?? false}
            />

            <TranslationBoostButton
              postId={post.id}
              count={translationCount}
              onOpenModal={() => {
                if (!isAuthenticated) { loginRedirect(); return }
                setShowTranslationModal(true)
              }}
            />

            {!isOwn && isOffer && (
              <button
                type="button"
                onClick={() => {
                  if (!isAuthenticated) { loginRedirect(); return }
                  navigate(
                    `/messages/${post.account.username}?ref=${post.id}&type=bazaar&title=${encodeURIComponent(post.content.slice(0, 50))}`,
                  )
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '6px 12px',
                  border: '1px solid var(--brand)',
                  background: 'none',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-pill)',
                  color: 'var(--brand)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-xs)',
                  fontWeight: 600,
                  transition: 'background var(--dur-fast) var(--ease-out)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--brand) 10%, transparent)' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}
              >
                {t('bazaar.interested')}
              </button>
            )}

            <ShareButton
              postId={post.id}
              authorUsername={post.account.username}
              contentPreview={post.content}
            />

            {!isOwn && (
              <ClapButton postId={post.id} clapped={post.clapped ?? false} />
            )}

            <button
              type="button"
              aria-label="Report"
              onClick={() => {
                if (!isAuthenticated) { loginRedirect(); return }
                setShowReportModal(true)
                onReport?.(post.id)
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '7px 9px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                borderRadius: 'var(--radius-pill)',
                color: 'var(--text-muted)',
                marginInlineStart: 'auto',
                transition: 'background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'color-mix(in srgb, var(--danger) 12%, transparent)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none'
              }}
            >
              <Flag size={16} />
            </button>
          </div>
        )}

        {/* Translation boost indicator */}
        {!readOnly && !isNested && translationCount > 0 && (
          <div style={{ padding: '0 16px 10px' }}>
            <button
              type="button"
              onClick={() => setShowTranslationBoosts((v) => !v)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: '2px 0',
                fontFamily: 'var(--font-sans)',
                fontSize: 14,
                color: 'var(--text-muted)',
              }}
            >
              <Globe size={14} />
              {t('post.translations_available', { count: translationCount })}
              {showTranslationBoosts ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>

            {showTranslationBoosts && translations.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                {translations.map((tr) => (
                  <TranslationCard key={tr.post.id} translation={tr} readOnly={readOnly} hideOriginal isDetailPage={isDetailPage} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reply thread — rendered inside the card container so it sits flush */}
      {!readOnly && !isNested && repliesOpen && (
        <ReplyList
          postId={post.id}
          parentUsername={post.account.username}
          replyCount={post.replyCount ?? 0}
          postLanguage={post.language}
        />
      )}

      {/* Modals */}
      <TranslationBoostModal
        open={showTranslationModal}
        onClose={() => setShowTranslationModal(false)}
        postId={post.id}
        originalContent={post.content}
        originalLanguage={post.language}
        eventPlaceText={isEvent ? ((post as EventPost).event?.placeText ?? null) : undefined}
        onSuccess={() => {
          setTranslationCount((c) => c + 1)
          queryClient.invalidateQueries({ queryKey: ['post-translations', post.id] })
          queryClient.invalidateQueries({ queryKey: ['feed'] })
        }}
      />
      <ReportModal
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        postId={post.id}
      />
    </div>
  )
}
